/**
 * Structured Formula Evaluator for Phase 44+
 * 
 * Clean, type-safe evaluator for structured formula objects.
 * NO backward compatibility - uses new schema exclusively.
 */

import { ModifierFormulaObject } from '../types/character';
import { ComputedStats, CharacterData } from '../types/character';
import { roundUpDaggerheart } from './daggerheartFormulas';

/**
 * Type-safe structured formula evaluator
 * Replaces the old string-based parsing system completely
 */
export class StructuredFormulaEvaluator {
  private character: CharacterData;
  
  constructor(character: CharacterData) {
    this.character = character;
  }
  
  /**
   * Evaluate a structured formula object
   * 
   * @param formulaObj - The structured formula object
   * @param stats - Current computed stats for trait values
   * @returns The evaluated numeric result with Daggerheart rounding applied
   */
  evaluateFormulaObject(formulaObj: ModifierFormulaObject, stats: ComputedStats): number {
    try {
      switch (formulaObj.type) {
        case 'trait_direct':
          return this.evaluateTraitDirect(formulaObj, stats);
          
        case 'trait_half':
          return this.evaluateTraitHalf(formulaObj, stats);
          
        case 'trait_multiply':
          return this.evaluateTraitMultiply(formulaObj, stats);
          
        case 'character_level':
          return this.evaluateCharacterLevel();
          
        case 'character_tier':
          return this.evaluateCharacterTier();
          
        case 'expression':
          return this.evaluateExpression(formulaObj, stats);
          
        default:
          // TypeScript should catch this, but fail fast if not
          throw new Error(`Unknown formula type: ${(formulaObj as any).type}`);
      }
    } catch (error) {
      console.warn(`Failed to evaluate structured formula, returning 0:`, formulaObj, error);
      return 0; // Graceful degradation - return 0 on formula evaluation failure
    }
  }
  
  /**
   * Evaluate direct trait reference (e.g., "agility" or "strength + 3")
   */
  private evaluateTraitDirect(formulaObj: ModifierFormulaObject, stats: ComputedStats): number {
    if (!formulaObj.trait) {
      throw new Error(`trait_direct formula missing trait property: ${JSON.stringify(formulaObj)}`);
    }

    const traitValue = stats.traits[formulaObj.trait];
    if (typeof traitValue !== 'number') {
      throw new Error(`Invalid trait value for ${formulaObj.trait}: ${traitValue}`);
    }

    // Add base value modifier if provided (e.g., "strength + 3" for bare-bones)
    const baseValue = formulaObj.value || 0;
    return traitValue + baseValue;
  }
  
  /**
   * Evaluate half trait value with Daggerheart rounding (e.g., "half agility")
   */
  private evaluateTraitHalf(formulaObj: ModifierFormulaObject, stats: ComputedStats): number {
    if (!formulaObj.trait) {
      throw new Error(`trait_half formula missing trait property: ${JSON.stringify(formulaObj)}`);
    }
    
    const traitValue = stats.traits[formulaObj.trait];
    if (typeof traitValue !== 'number') {
      throw new Error(`Invalid trait value for ${formulaObj.trait}: ${traitValue}`);
    }
    
    // Apply Daggerheart rounding rule: all fractions round up
    return roundUpDaggerheart(traitValue / 2);
  }
  
  /**
   * Evaluate trait multiplication (e.g., "agility * 2")
   */
  private evaluateTraitMultiply(formulaObj: ModifierFormulaObject, stats: ComputedStats): number {
    if (!formulaObj.trait) {
      throw new Error(`trait_multiply formula missing trait property: ${JSON.stringify(formulaObj)}`);
    }
    
    if (typeof formulaObj.multiplier !== 'number') {
      throw new Error(`trait_multiply formula missing multiplier property: ${JSON.stringify(formulaObj)}`);
    }
    
    const traitValue = stats.traits[formulaObj.trait];
    if (typeof traitValue !== 'number') {
      throw new Error(`Invalid trait value for ${formulaObj.trait}: ${traitValue}`);
    }
    
    const result = traitValue * formulaObj.multiplier;
    
    // Apply Daggerheart rounding if result is fractional
    return result % 1 === 0 ? result : roundUpDaggerheart(result);
  }
  
  /**
   * Evaluate character level
   */
  private evaluateCharacterLevel(): number {
    const level = parseInt(this.character.level) || 1;
    if (level < 1 || level > 10) {
      throw new Error(`Invalid character level: ${this.character.level}`);
    }
    return level;
  }
  
  /**
   * Evaluate character tier
   */
  private evaluateCharacterTier(): number {
    const tier = this.character.tier || 1;
    if (tier < 1 || tier > 4) {
      throw new Error(`Invalid character tier: ${this.character.tier}`);
    }
    return tier;
  }
  
  /**
   * Evaluate custom expression (fallback for complex formulas)
   */
  private evaluateExpression(formulaObj: ModifierFormulaObject, stats: ComputedStats): number {
    if (!formulaObj.formula) {
      throw new Error(`expression formula missing formula property: ${JSON.stringify(formulaObj)}`);
    }

    // Dependencies are optional - formulas with no dependencies are allowed (e.g., constants)
    // The safety validation below will catch malicious code
    
    // Replace variables in the expression
    let expression = formulaObj.formula;

    // Replace character references (full path)
    expression = expression.replace(/\bcharacter\.level\b/g, this.character.level || '1');
    expression = expression.replace(/\bcharacter\.tier\b/g, String(this.character.tier || 1));

    // Replace trait references (full path: stats.traits.X)
    expression = expression.replace(/\bstats\.traits\.(\w+)\b/g, (match, traitName) => {
      const value = (stats.traits as any)[traitName];
      return typeof value === 'number' ? String(value) : '0';
    });

    // Replace shorthand trait references (traits.X)
    expression = expression.replace(/\btraits\.(\w+)\b/g, (match, traitName) => {
      const value = (stats.traits as any)[traitName];
      return typeof value === 'number' ? String(value) : '0';
    });

    // Replace shorthand tier reference
    expression = expression.replace(/\btier\b/g, String(this.character.tier || 1));

    // Replace shorthand level reference
    expression = expression.replace(/\blevel\b/g, String(this.character.level || 1));
    
    // Safety validation - only allow safe operations
    if (!/^[0-9+\-*/.()Math\s]+$/.test(expression.replace(/parseInt|roundUpDaggerheart|Math\.(ceil|floor|round|min|max)/g, ''))) {
      throw new Error(`Unsafe expression: ${expression}`);
    }
    
    try {
      // Create safe evaluation context
      const context = {
        parseInt: parseInt,
        roundUpDaggerheart: roundUpDaggerheart,
        Math: {
          ceil: Math.ceil,
          floor: Math.floor,
          round: Math.round,
          min: Math.min,
          max: Math.max
        }
      };
      
      // Evaluate with context
      const func = new Function(...Object.keys(context), `"use strict"; return (${expression})`);
      const result = func(...Object.values(context));
      
      if (typeof result !== 'number' || isNaN(result)) {
        throw new Error(`Expression evaluation did not return a valid number: ${result}`);
      }
      
      return result;
    } catch (error) {
      throw new Error(`Failed to evaluate expression "${expression}": ${error}`);
    }
  }
  
  /**
   * Get human-readable description of a formula for tooltips
   */
  getFormulaDescription(formulaObj: ModifierFormulaObject, stats: ComputedStats): string {
    const result = this.evaluateFormulaObject(formulaObj, stats);
    
    switch (formulaObj.type) {
      case 'trait_direct':
        return `${formulaObj.trait} (${result})`;
        
      case 'trait_half':
        const halfValue = stats.traits[formulaObj.trait!] / 2;
        return `½ ${formulaObj.trait} (${halfValue} → ${result})`;
        
      case 'trait_multiply':
        return `${formulaObj.trait} × ${formulaObj.multiplier} (${result})`;
        
      case 'character_level':
        return `character level (${result})`;
        
      case 'character_tier':
        return `character tier (${result})`;
        
      case 'expression':
        return `${formulaObj.formula} (${result})`;
        
      default:
        return `unknown formula (${result})`;
    }
  }
}

/**
 * Helper function to determine if a value is a structured formula object
 */
export function isStructuredFormula(value: any): value is ModifierFormulaObject {
  return typeof value === 'object' && 
         value !== null && 
         typeof value.type === 'string' &&
         ['trait_direct', 'trait_half', 'trait_multiply', 'character_level', 'character_tier', 'expression'].includes(value.type);
}

/**
 * Helper function to get dependencies for a structured formula
 */
export function getFormulaDependencies(formulaObj: ModifierFormulaObject): string[] {
  switch (formulaObj.type) {
    case 'trait_direct':
    case 'trait_half':
    case 'trait_multiply':
      return [`traits.${formulaObj.trait}`];
      
    case 'character_level':
      return ['level'];
      
    case 'character_tier':
      return ['tier'];
      
    case 'expression':
      return formulaObj.dependencies || [];
      
    default:
      return [];
  }
}